import { useEffect, useState } from 'react';
import { api, can, getUser } from '../../../lib/api';
import { Card, Empty, Field, useToast } from '../../../components/ui';
import type { GeoRule, PointRow } from '../types';

/**
 * The banner's region rules. Ordered; the first rule covering a visitor's
 * region decides the consent model they see; with no match the banner asks
 * for opt-in. Regions are ISO 3166-1 alpha-2 codes, EU for the member states,
 * or * for any region.
 */

const MODEL_LABEL: Record<GeoRule['model'], string> = {
  opt_in: 'Opt in',
  notice_only: 'Notice only',
  opt_out: 'Opt out',
};

const MODEL_NOTE: Record<GeoRule['model'], string> = {
  opt_in: 'Nothing but essential runs until allowed',
  notice_only: 'Notice bar; every category runs',
  opt_out: 'Every category runs until switched off',
};

const regionNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

/** "DE · Germany", "EU · European Union", "* · Any region". */
function regionLabel(code: string): string {
  if (code === '*') return 'Any region';
  if (code === 'EU') return 'EU · European Union';
  let name = '';
  try { name = regionNames?.of(code) ?? ''; } catch { name = ''; }
  return name && name !== code ? `${code} · ${name}` : code;
}

function parseRegions(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean))];
}

export default function Regions({ banner, onChanged }: { banner: PointRow; onChanged: () => void }) {
  const toast = useToast();
  const editable = can(getUser(), 'cookies:edit');

  const [rules, setRules] = useState<GeoRule[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [regionsText, setRegionsText] = useState('');
  const [model, setModel] = useState<GeoRule['model']>('opt_in');

  useEffect(() => {
    setRules(banner.geoRules ?? []);
    setDirty(false);
  }, [banner.id, banner.geoRules]);

  const draftRegions = parseRegions(regionsText);
  const draftInvalid = draftRegions.filter((r) => !/^([A-Z]{2}|\*)$/.test(r));

  function addRule() {
    if (!draftRegions.length || draftInvalid.length) return;
    setRules([...rules, { regions: draftRegions, model }]);
    setRegionsText('');
    setDirty(true);
  }

  function removeRule(i: number) {
    setRules(rules.filter((_, k) => k !== i));
    setDirty(true);
  }

  function move(i: number, by: -1 | 1) {
    const j = i + by;
    if (j < 0 || j >= rules.length) return;
    const next = [...rules];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setRules(next);
    setDirty(true);
  }

  async function save() {
    setBusy(true);
    try {
      await api.put(`/v1/collection-points/${banner.id}/geo-rules`, { rules });
      toast('Region rules saved.', 'ok');
      setDirty(false);
      onChanged();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Regions">
      {rules.length === 0 ? <Empty>No rules. Every visitor is asked to opt in.</Empty> : (
        <div className="table-scroll">
          <table className="data ck-table">
            <thead>
              <tr>
                <th className="num">Order</th>
                <th>Regions</th>
                <th>Model</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={`${i}-${r.regions.join(',')}`}>
                  <td className="num">{i + 1}</td>
                  <td>{r.regions.map(regionLabel).join(', ')}</td>
                  <td>
                    {MODEL_LABEL[r.model]}
                    <span className="small muted"> · {MODEL_NOTE[r.model]}</span>
                  </td>
                  {editable && (
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      <button className="small" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>{' '}
                      <button className="small" onClick={() => move(i, 1)} disabled={i === rules.length - 1} aria-label="Move down">↓</button>{' '}
                      <button className="small" onClick={() => removeRule(i)}>Remove rule</button>
                    </td>
                  )}
                </tr>
              ))}
              <tr>
                <td className="num muted">{rules.length + 1}</td>
                <td className="muted">Any other region</td>
                <td className="muted">Opt in<span className="small"> · {MODEL_NOTE.opt_in}</span></td>
                {editable && <td />}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {editable && (
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 180px auto', gap: 10, alignItems: 'end' }}>
          <Field label="Regions" help="Two-letter ISO codes, EU, or *. Comma separated.">
            <input
              type="text"
              value={regionsText}
              placeholder="IN, EU"
              onChange={(e) => setRegionsText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRule(); } }}
              aria-invalid={draftInvalid.length > 0}
            />
          </Field>
          <Field label="Model">
            <select value={model} onChange={(e) => setModel(e.target.value as GeoRule['model'])}>
              {(Object.keys(MODEL_LABEL) as GeoRule['model'][]).map((m) => <option key={m} value={m}>{MODEL_LABEL[m]}</option>)}
            </select>
          </Field>
          <div className="field">
            <button onClick={addRule} disabled={!draftRegions.length || draftInvalid.length > 0}>Add rule</button>
          </div>
        </div>
      )}
      {draftInvalid.length > 0 && <div className="note bad">Not a region code: {draftInvalid.join(', ')}</div>}

      {editable && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="primary" onClick={save} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save rules'}</button>
          {dirty && <button onClick={() => { setRules(banner.geoRules ?? []); setDirty(false); }} disabled={busy}>Cancel</button>}
        </div>
      )}
    </Card>
  );
}
